import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Alert, MenuItem, TextField, Divider, Chip } from "@mui/material";
import { paymentsApi } from "./api";
import ConfirmDialog from "./ConfirmDialog";

type Props = {
  open: boolean;
  parentId: string | number;
  studentId: number;
  agreementId: number;
  onClose: () => void;
  onSuccess: () => void;
};

const AutoPayManageDialog: React.FC<Props> = ({ open, parentId, studentId, agreementId, onClose, onSuccess }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [ag, setAg] = React.useState<any | null>(null);
  const [history, setHistory] = React.useState<any[]>([]);
  const [cards, setCards] = React.useState<any[]>([]);
  const [newCardId, setNewCardId] = React.useState<number | "">("");
  const [confirmCancel, setConfirmCancel] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const list = await paymentsApi.autoPayList(parentId);
        const found = (list?.data ?? []).find((a: any) => Number(a.id) === Number(agreementId));
        setAg(found ?? null);
        try {
          const h = await paymentsApi.autoPayHistory(agreementId);
          setHistory(h?.data ?? []);
        } catch { setHistory([]); }
        if (found?.payer_parent_id ?? found?.payment_method_id) {
          const c = await paymentsApi.cardsForParent(found.payer_parent_id ?? (parentId as number));
          setCards(c?.data ?? []);
          setNewCardId(found.payment_method_id);
        }
      } catch (e: any) {
        setError(e?.message ?? "Failed to load agreement.");
      }
    })();
  }, [open, parentId, agreementId]);

  const wrap = async (fn: () => Promise<any>) => {
    setBusy(true); setError(null);
    try { await fn(); onSuccess(); }
    catch (e: any) { setError(e?.message ?? "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Manage Auto-Pay</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!ag ? (
            <Typography>Loading…</Typography>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip label={ag.status} color={ag.status === "active" ? "success" : ag.status === "paused" ? "warning" : "error"} />
                <Typography variant="body2">Billing day: <strong>{ag.billing_day}</strong></Typography>
                <Typography variant="body2">Next charge: <strong>{ag.next_charge_date ?? "—"}</strong></Typography>
                <Typography variant="body2">Card: <strong>{ag.cardBrand} •••• {ag.cardLast4}</strong></Typography>
              </Stack>

              <Divider>Change card</Divider>
              <Stack direction="row" spacing={2}>
                <TextField select fullWidth size="small" label="New card" value={newCardId} onChange={(e) => setNewCardId(Number(e.target.value))} disabled={busy}>
                  {cards.map((c) => <MenuItem key={c.id} value={c.id}>{c.cardBrand} •••• {c.cardLast4}</MenuItem>)}
                </TextField>
                <Button variant="outlined" disabled={busy || !newCardId || newCardId === ag.payment_method_id}
                  onClick={() => wrap(() => paymentsApi.autoPayChangeCard(studentId, agreementId, newCardId as number))}
                >Save Card</Button>
              </Stack>

              <Divider>Actions</Divider>
              <Stack direction="row" spacing={2}>
                {ag.status === "active" && (
                  <Button variant="outlined" color="warning" disabled={busy}
                    onClick={() => wrap(() => paymentsApi.autoPayPause(studentId, agreementId))}
                  >Pause</Button>
                )}
                {ag.status === "paused" && (
                  <Button variant="outlined" color="primary" disabled={busy}
                    onClick={() => wrap(() => paymentsApi.autoPayResume(studentId, agreementId))}
                  >Resume</Button>
                )}
                {ag.status !== "cancelled" && (
                  <Button variant="outlined" color="error" disabled={busy} onClick={() => setConfirmCancel(true)}>Cancel Auto-Pay</Button>
                )}
              </Stack>

              <Divider>History</Divider>
              <Stack spacing={1} sx={{ maxHeight: 240, overflow: "auto" }}>
                {history.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No history yet.</Typography>
                ) : history.map((h) => (
                  <Typography key={h.id} variant="body2">
                    {h.happened_at?.slice(0, 19).replace("T", " ")} · <strong>{h.action}</strong>
                    {h.old_status && h.new_status && <> · {h.old_status} → {h.new_status}</>}
                    {h.reason && <> · {h.reason}</>}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={busy}>Close</Button>
        </DialogActions>
      </Dialog>

      {confirmCancel && (
        <ConfirmDialog open title="Cancel Auto-Pay" requireReason
          message="This will stop future automatic charges. Existing successful payments are unaffected."
          confirmLabel="Cancel Agreement" confirmColor="error"
          onClose={() => setConfirmCancel(false)}
          onConfirm={async (reason) => {
            await paymentsApi.autoPayCancel(studentId, agreementId, reason ?? "");
            setConfirmCancel(false);
            onSuccess();
          }}
        />
      )}
    </>
  );
};

export default AutoPayManageDialog;
